/**
 * Shared file-upload / attachment utilities.
 * Single place for file-type detection, preview URLs, and download helpers.
 * Used by: useAttachments, AttachmentPreviewPopover, AttachmentCarousel.
 */
import type { AttachmentItem, SavedAttachment } from '../types/attachments';

/** MIME pattern for image types (used for preview URL and carousel) */
export const IMAGE_TYPES_REGEX = /^image\//i;

export type AttachmentLike = AttachmentItem | SavedAttachment | File;

function getType(value: AttachmentLike): string {
  if (value instanceof File) return value.type ?? '';
  const item = value as AttachmentItem | SavedAttachment;
  return item.type ?? (item as { file?: File }).file?.type ?? '';
}

function getName(value: AttachmentLike): string {
  if (value instanceof File) return value.name ?? '';
  const item = value as AttachmentItem | SavedAttachment;
  return item.name ?? (item as { file?: File }).file?.name ?? '';
}

/** Use for display when item may have only `file` (name/type derived from file). */
export { getName as getAttachmentName, getType as getAttachmentType };

export function isImageFile(value: AttachmentLike): boolean {
  return IMAGE_TYPES_REGEX.test(getType(value));
}

export function isPdfFile(item: AttachmentItem | SavedAttachment): boolean {
  const type = getType(item).toLowerCase();
  const name = getName(item).toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
}

export interface FileTypeInfo {
  label: string;
  slug: string;
}

/** Returns a short label and CSS slug for UI (badge, carousel). */
export function getFileTypeInfo(
  item: AttachmentItem | SavedAttachment
): FileTypeInfo {
  const type = getType(item).toLowerCase();
  const name = getName(item).toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';

  if (isImageFile(item) || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name)) {
    return { label: 'IMG', slug: 'image' };
  }
  if (type === 'application/pdf' || ext === 'pdf')
    return { label: 'PDF', slug: 'pdf' };
  if (
    type.includes('wordprocessingml') ||
    type === 'application/msword' ||
    ext === 'doc' ||
    ext === 'docx'
  ) {
    return { label: 'DOC', slug: 'doc' };
  }
  if (
    type.includes('spreadsheet') ||
    type === 'application/vnd.ms-excel' ||
    /^(xls|xlsx)$/.test(ext)
  ) {
    return { label: 'XLS', slug: 'xls' };
  }
  if (
    type.includes('presentation') ||
    type === 'application/vnd.ms-powerpoint' ||
    ext === 'ppt' ||
    ext === 'pptx'
  ) {
    return { label: 'PPT', slug: 'ppt' };
  }
  if (type.includes('text/plain') || ext === 'txt')
    return { label: 'TXT', slug: 'txt' };
  if (type.includes('text/') || ext === 'csv')
    return { label: 'TXT', slug: 'txt' };
  if (type.includes('video/') || /\.(mp4|webm|mov|avi|mkv)$/i.test(name)) {
    return { label: 'VID', slug: 'video' };
  }
  if (type.includes('audio/') || /\.(mp3|wav|ogg|m4a)$/i.test(name)) {
    return { label: 'AUD', slug: 'audio' };
  }
  return { label: 'FILE', slug: 'file' };
}

/** Creates an object URL for image preview; returns undefined for non-images. Caller must revoke. */
export function createImagePreviewUrl(file: File): string | undefined {
  if (IMAGE_TYPES_REGEX.test(file.type)) {
    return URL.createObjectURL(file);
  }
  return undefined;
}

/** Generates a stable unique id for an attachment item. */
export function generateAttachmentId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Triggers a download of the file via a temporary blob URL (creates and revokes URL). */
export function triggerBlobDownload(file: File, filename: string): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
